import type { SVGProps } from 'react';

/**
 * The one linear icon set (review §5): every pictogram in the product is an
 * inline stroke-based SVG on a 24px grid — 1.75px stroke, round caps,
 * currentColor — so icons inherit text colour and scale like type. Character
 * glyphs (←, ✕, ✓, ↕, ⚡ …) must never be used as icons.
 *
 * Deliberately a local file, not an icon library: the whole product needs a
 * dozen icons and zero extra dependencies.
 */
function Icon({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconArrowLeft(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M19 12H5" />
      <path d="m11 18-6-6 6-6" />
    </Icon>
  );
}

export function IconClose(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Icon>
  );
}

export function IconCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="m4.5 12.5 5.5 5.5L19.5 7" />
    </Icon>
  );
}

export function IconAlert(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M12 5v9" />
      <path d="M12 18.5h.01" />
    </Icon>
  );
}

export function IconMinus(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
    </Icon>
  );
}

export function IconPlus(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Icon>
  );
}

/** Selected radio: stroked ring with a filled core — still one-set, one-weight. */
export function IconRadioOn(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.75" />
      <circle cx="12" cy="12" r="4.25" fill="currentColor" stroke="none" />
    </Icon>
  );
}

export function IconRadioOff(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.75" />
    </Icon>
  );
}

/* --- Facility icons (lot amenities and structured facts) ------------------ */

export function IconClock(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  );
}

export function IconUser(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </Icon>
  );
}

export function IconArrowDownToLine(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M12 4v11.5" />
      <path d="m7 11 5 4.5 5-4.5" />
      <path d="M5 20h14" />
    </Icon>
  );
}

export function IconHome(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="m4 10.5 8-6.5 8 6.5V20H4Z" />
    </Icon>
  );
}

export function IconSun(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.8 5.8l1.4 1.4M16.8 16.8l1.4 1.4M18.2 5.8l-1.4 1.4M7.2 16.8l-1.4 1.4" />
    </Icon>
  );
}

export function IconArrowsVertical(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M12 4.5v15" />
      <path d="m8.5 8 3.5-3.5L15.5 8" />
      <path d="m8.5 16 3.5 3.5 3.5-3.5" />
    </Icon>
  );
}

export function IconElevator(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M8 19.5V5" />
      <path d="M5 8l3-3 3 3" />
      <path d="M16 4.5V19" />
      <path d="m13 16 3 3 3-3" />
    </Icon>
  );
}

export function IconCamera(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="3" y="7" width="13" height="10" rx="2" />
      <path d="m16 10.5 5-2.5v8l-5-2.5" />
    </Icon>
  );
}

export function IconBolt(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M13 3 5 13.5h5.5L11 21l8-10.5h-5.5Z" />
    </Icon>
  );
}

export function IconAccessible(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="13" cy="4.5" r="1.75" />
      <path d="M13 7.5V13h4.5l2 4.5" />
      <path d="M10 10.7a5.5 5.5 0 1 0 7 6.8" />
    </Icon>
  );
}

export function IconUmbrella(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5H3.5A8.5 8.5 0 0 1 12 3.5Z" />
      <path d="M12 12v6a2 2 0 0 0 4 0" />
    </Icon>
  );
}

export function IconGrid(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <path d="M4 12h16M12 4v16" />
    </Icon>
  );
}

/** Fallback for amenity codes the client does not know yet. */
export function IconDot(props: SVGProps<SVGSVGElement>) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    </Icon>
  );
}
